import React, { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../routing/Routing";
import {
  ERRORS,
  ONBOARDING_STEPS,
  ROUTES,
  VIEWS,
} from "../constants/user-info.const";
import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import FederalTaxesForm from "./user-forms/FederalTaxesForm";
import DownwardArrow from "../../assets/icons-svg/downward-arrow.svg";
import { FederalTaxFormFields } from "./models/user-interface";
import Loader from "../common/Loader";
import {
  getFederalTaxes,
  updateFederalTaxes,
} from "../../services/api-services/federalTaxService";
import { connect } from "react-redux";
import store from "../../store";
import { showAlert } from "../../store/actions/feedback";
import { onboardingActions } from "../../store/actions/steps";
import { formSubmitErrorHandler } from "../../services/formService";
import debounce from "../../utils/debounce";
import hideScroll from "../../utils/hideScroll";

function FederalTaxes(props: any) {
  const { filingStatus, isNavigateToNext } = props;
  const { viewName, setViewName } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(true);
  const [isShowFooterLoader, setIsShowFooterLoader] = useState<boolean>(false);
  const [employeeData, setEmployeeData] = useState<any>({});
  const navigate = useNavigate();
  const documentScrollHeight = document.documentElement.scrollHeight;
  const [showScroller, setShowScroller] = useState(true);
  const mountedRef = useRef(true);

  const validationSchema = Yup.object({
    filing_status: Yup.string().required(ERRORS.filing_status),
    deductions: Yup.number()
      .max(1000000000, ERRORS.deductionAmountMaxValue)
      .min(0, ERRORS.deductionAmountMinValue),
    dependents_amount: Yup.number()
      .max(1000000000, ERRORS.dependentAmountMaxValue)
      .min(0, ERRORS.dependentAmountMinValue),
    other_income: Yup.number()
      .max(1000000000, ERRORS.otherIncomeMaxValue)
      .min(0, ERRORS.otherIncomeMinValue),
    extra_withholding: Yup.number()
      .max(1000000000, ERRORS.withHoldingAmountMaxValue)
      .min(0, ERRORS.withHoldingAmountMinValue),
  });

  const getFormData = (data: FederalTaxFormFields) => {
    const inputs = {
      version: employeeData?.version,
      filing_status: data?.filing_status,
      dependents_amount: data?.dependents_amount,
      other_income: data?.other_income,
      two_jobs: data?.two_jobs === "Yes" ? true : false,
      deductions: data?.deductions,
      extra_withholding: data?.extra_withholding,
    };
    return inputs; 
  };
  const showErrorMessage = (error) => {
    const errorMessage = error.status?.message;
    store.dispatch(
      showAlert({
        alertType: "error",
        alertText: errorMessage,
      })
    );
  };

  useEffect(() => {
    getFederalTaxes()
      .then((response: any) => {
        if (!mountedRef.current) return null;
        setIsLoading(false);
        response.status === 200 && setEmployeeData(response.data);
      })
      .catch((error) => {
        if (!mountedRef.current) return null;
        setIsLoading(false);
        showErrorMessage(error);
      });
  }, []);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const updateCompletedStep = () => {
    store.dispatch({
      type: onboardingActions.UPDATE_ONBOARDING_STEPS,
      state: ONBOARDING_STEPS.federalTaxes
    });
  };
  const navigateToStateTaxes = () => {
    if(isNavigateToNext === false) return;
    setViewName(VIEWS.stateTaxes);
    navigate(ROUTES.stateTaxes);
  };

  const onSubmit = (
    data: FederalTaxFormFields,
    formHelpers: FormikHelpers<any>
  ) => {
    const formInputs = getFormData(data);
    setIsShowFooterLoader(true);
    formSubmitErrorHandler(
      updateFederalTaxes(formInputs).then((res : any) => {
        setIsShowFooterLoader(false);
        setEmployeeData(res.data);
        navigateToStateTaxes();
        updateCompletedStep();
        formHelpers.resetForm();
      }),
      () => {
        setIsShowFooterLoader(false);
      },
      formHelpers.setFieldError
    );
  };
  const hideScrollButton = debounce(
    () =>
      hideScroll(
        () => setShowScroller(false),
        () => setShowScroller(true)
      ),
    50
  );

  useEffect(() => {
    window.addEventListener("scroll", hideScrollButton);
    return () => {
      window.removeEventListener("scroll", hideScrollButton);
    };
  }, []);
  const scrollToBottom = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    window.scrollTo({
      top: documentScrollHeight,
      behavior: "smooth",
    });
  };
  return (
    <div className="federal-taxes user-details__content">
      <Formik
        initialValues={{
          filing_status: employeeData?.filing_status ?? "",
          dependents_amount: employeeData?.dependents_amount ?? "0",
          other_income: employeeData?.other_income ?? "0.00",
          two_jobs: employeeData?.two_jobs ? "Yes" : "No",
          deductions: employeeData?.deductions ?? "0.00",
          extra_withholding: employeeData?.extra_withholding ?? "0.00",
        }}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        innerRef={props.formRef}
      >
        {() => {
          return (
            <>
              <Form>
                <div className="user-details--section">
                  {isLoading ? (
                    <Loader />
                  ) : (
                    <FederalTaxesForm filingStatus={filingStatus} />
                  )}
                  {isShowFooterLoader && <Loader footerLoader={true}/>}
                </div>
              </Form>
              {showScroller && !isShowFooterLoader && (
                <div className="scroll-arrow" onClick={scrollToBottom}>
                  <img src={DownwardArrow} alt="Scroll Down" />
                </div>
              )}
            </>
          );
        }}
      </Formik>
    </div>
  );
}
const mapStateToProps = (state) => ({
  filingStatus: state.config?.employees?.federal_taxes?.filing_statuses,
  isNavigateToNext: state.navigationFromMenu.isNavigateToNext
});

export default connect(mapStateToProps)(FederalTaxes);
